export const ENVIRONMENT_IMAGE_PICKER_KINDS = [
  "base_image",
  "container_image",
  "application_image",
  "custom_image",
] as const;

export type EnvironmentImagePickerKind = (typeof ENVIRONMENT_IMAGE_PICKER_KINDS)[number];

export type EnvironmentImagePickerRecordBase = {
  id: number | string;
  ref_id: number | string;
  ref_code?: string | null;
  ref_name: string;
  image_kind?: string | null;
  description?: string | null;
  status?: string | null;
  category?: string | null;
  share_mode?: string | null;
  default_spec_title?: string | null;
  architecture?: string | null;
  usage_type?: string | null;
};

export function isEnvironmentImagePickerKind(value?: string | null): value is EnvironmentImagePickerKind {
  return ENVIRONMENT_IMAGE_PICKER_KINDS.includes(value as EnvironmentImagePickerKind);
}

export function normalizeAllowedImageKinds(
  allowedImageKinds: readonly EnvironmentImagePickerKind[],
): readonly EnvironmentImagePickerKind[] {
  if (allowedImageKinds.length === 0) {
    throw new Error("ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: allowedImageKinds must not be empty");
  }
  for (const kind of allowedImageKinds) {
    if (!isEnvironmentImagePickerKind(kind)) {
      throw new Error(`ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: unsupported image kind ${kind}`);
    }
  }
  return allowedImageKinds;
}

export function readEnvironmentImagePickerKind(
  selected: Pick<EnvironmentImagePickerRecordBase, "image_kind">,
): EnvironmentImagePickerKind {
  if (!isEnvironmentImagePickerKind(selected.image_kind)) {
    throw new Error(`ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: invalid image_kind ${selected.image_kind ?? "<empty>"}`);
  }
  return selected.image_kind;
}

export function formatEnvironmentImagePickerDisplay(selected: Pick<EnvironmentImagePickerRecordBase, "ref_id" | "ref_code" | "ref_name">) {
  const name = selected.ref_name.trim();
  const ref = String(selected.ref_code || selected.ref_id).trim();
  if (!name || !ref) {
    throw new Error("ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: missing ref_name or ref_code/ref_id");
  }
  return `${name} (${ref})`;
}

export function readEnvironmentImagePickerRefId(selected: Pick<EnvironmentImagePickerRecordBase, "ref_id">) {
  const refId = Number(selected.ref_id);
  if (!Number.isInteger(refId) || refId < 1) {
    throw new Error(`ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: invalid ref_id ${selected.ref_id}`);
  }
  return refId;
}
